import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { env } from '@/env/client';
import { useAuthStore } from '@/stores/auth';
import { useAuthActions } from '@convex-dev/auth/react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface LoginProps {
  onContinue: (email: string) => void;
}

export const Login = ({ onContinue }: LoginProps) => {
  const { t } = useTranslation();
  const { signIn } = useAuthActions();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearAuth = useAuthStore(
    (state: { clearAuth: () => void }) => state.clearAuth,
  );

  const isReviewAccount =
    email.toLowerCase().trim() === env.VITE_REVIEW_ACCOUNT_EMAIL.toLowerCase();

  const handlePasswordSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.set('email', email);
      formData.set('password', password);
      formData.set('flow', 'signIn');
      await signIn('password', formData);
      clearAuth();
    } catch {
      setError(t('errors:failedToSignIn'));
      setPassword('');
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.set('email', email);
      await signIn('resend-otp', formData);
      onContinue(email);
    } catch {
      setError(t('errors:failedToSendCode'));
    } finally {
      setIsLoading(false);
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut' as const,
      },
    },
  };

  return (
    <div className="flex w-full flex-col items-center justify-center gap-8">
      <motion.div
        className="flex flex-col items-center justify-center gap-2"
        variants={itemVariants}>
        <h1 className="text-3xl font-bold">{t('login:title')}</h1>
        <p className="text-muted-foreground text-center text-sm">
          {t('login:subtitle')}
        </p>
      </motion.div>
      <motion.form
        onSubmit={isReviewAccount ? handlePasswordSubmit : handleSubmit}
        className="flex w-full flex-col gap-4"
        variants={itemVariants}>
        <Input
          type="email"
          placeholder={t('login:email')}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setPassword('');
            setError(null);
          }}
          required
          disabled={isLoading}
        />
        {isReviewAccount && (
          <Input
            type="password"
            placeholder={t('login:password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isLoading}
          />
        )}
        {error && <p className="text-destructive text-sm">{error}</p>}
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? t('login:sending') : t('login:continue')}
        </Button>
      </motion.form>
    </div>
  );
};
